import React from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet';
import {graphql, Link} from 'gatsby';
import Breadcrumbs from '@homeaway/react-breadcrumbs';
import RichText from '../RichText/RichText';
import PrintButton from '../PrintButton/PrintButton';
import Layout from '../Layout/Layout';
import types from '../../types';
import {checkIfIsSingaporeButNotEnglish, getSlug} from '../../utils';

export default function Document({data, pageContext, location}) {
    const {sitename, contentfulLocale, allLocales} = pageContext;
    const {title, body, hidden} = data.contentfulDocument;
    const {legalInformation, printBtnLabel} = data.contentfulTranslations;
    const homePath = checkIfIsSingaporeButNotEnglish(sitename, contentfulLocale)
        ? getSlug(contentfulLocale)
        : '';
    const crumbs = [
        {
            name: legalInformation,
            url: '',
            node: <Link to={`/${homePath}`} />,
        },
        {
            name: title,
            url: '',
        },
    ];

    const label = `${printBtnLabel} ${title}`;

    const handleClick = () => {
        window.print();
    };

    return (
        <Layout
            pathname={location.pathname}
            contentfulLocale={contentfulLocale}
            allLocales={allLocales}
            localizedDocumentLinks={data.localizedDocumentLinks.nodes}
        >
            <Helmet>
                <title>{title}</title>
                {hidden && (
                    <meta name="robots" content="noindex,nofollow, noarchive" />
                )}
            </Helmet>
            <Breadcrumbs crumbs={crumbs} />
            <PrintButton onClick={handleClick} label={label} />
            <h1 className="Legal__page-title">{title}</h1>
            <RichText body={body} />
        </Layout>
    );
}

Document.propTypes = {
    data: PropTypes.shape({
        contentfulDocument: PropTypes.shape({
            id: PropTypes.string,
            title: PropTypes.string.isRequired,
            body: PropTypes.shape({
                raw: PropTypes.object,
            }).isRequired,
        }),
        localizedDocumentLinks: PropTypes.shape({
            nodes: types.documentLinkArray,
        }),
        contentfulTranslations: PropTypes.shape({
            legalInformation: PropTypes.string.isRequired,
            printBtnLabel: PropTypes.string.isRequired,
        }),
    }).isRequired,
    pageContext: PropTypes.shape({
        sitename: PropTypes.string.isRequired,
        contentfulLocale: PropTypes.string.isRequired,
        allLocales: types.allLocales,
    }),
};

// FIXME new locales w/o references to both ContentfulDocument and ContentfulMarkdownTable will make this query explode with error:
//       "Fragment cannot be spread here as objects of type "ContentfulDocument" can never be of type "ContentfulMarkdownTable"."
//
//       As a workaround duplicate the contents of the following document for the new locale, set as hidden and publish:
//       https://app.contentful.com/spaces/za8yqak3t4q3/entries/2wAfcnyZbohDeoASXToiem
export const query = graphql`
    query Query($id: String, $contentfulId: String, $contentfulLocale: String) {
        contentfulDocument(id: {eq: $id}) {
            id
            title
            hidden
            body {
                raw
                references {
                    ... on ContentfulDocument {
                        contentful_id
                        node_locale
                        slug
                    }
                    ... on ContentfulMarkdownTable {
                        contentful_id
                    }
                }
            }
        }
        localizedDocumentLinks: allContentfulDocument(
            filter: {
                slug: {ne: null}
                body: {raw: {ne: null}}
                contentful_id: {eq: $contentfulId}
            }
            sort: {fields: title, order: ASC}
        ) {
            nodes {
                title
                route
                node_locale
            }
        }
        contentfulTranslations(node_locale: {eq: $contentfulLocale}) {
            legalInformation
            printBtnLabel
        }
    }
`;
